/**
 * FedRAMP 20x Class A checklist — V103-6-3 (design pass `v103-programs-ui`
 * round 4, `c-class-a.html`). The Prepare tab of a Class A FedRAMP program:
 *
 *   hero        ONE alarm and ONE primary for the world the page is in (the
 *               Ready materials, else the GovRAMP state it rests on), the
 *               rules done of the required set, meter + legend, and Fix /
 *               Collect / Start labelled from the SAME totals as the legend
 *               (acceptance #1: "Start N" = "N not started", else split
 *               "N to start · M waiting on a booking")
 *   materials   the alternative-framework materials (GovRAMP RAR + SAP, or a
 *               SOC 2 Type II report) with their 12-month expiry; upload or
 *               replace in place
 *   clocks      the Class A listing date (inline form), how long agencies can
 *               use Class A, the Class B booking
 *   checklist   the Class A rules from the rules dataset (FedRAMP requirements
 *               required for Class A; nothing hard-coded): one flat
 *               "Needs action" list, done folded, optional rules folded; rule
 *               ids only inside the "Why" disclosure (KSI ids stay on KSI rows)
 *
 * The Ready booking is the GovRAMP program's ONE record: recording it here
 * writes that row, and the GovRAMP page shows the same booking (acceptance #4).
 *
 * Reads (any member): Class A items, FedRAMP class readiness, the GovRAMP
 * overview, the alternative materials, program events. Writes (owner / admin
 * or the FedRAMP program lead; server-enforced, asked otherwise): materials
 * upload, listing date, Class B booking, the Ready booking.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Spinner } from '@aigovops/design-system';
import type { ProgramCard } from '../../lib/api';
import { invalidationMap, queryKeys } from '../../lib/query-keys';
import { usePrograms } from '../../lib/workspace-navigation';
import { listAlternativeMaterials, listProgramEvents, setClassAListing, uploadAlternativeMaterial } from '../../lib/govramp-api';
import { classALegend, daysText, fmtLong, fmtShort, govrampStatus, tierLabel, type ClassARule, type RuleStatus } from '../../lib/govramp-model';
import { useClassA } from '../../lib/use-class-a';
import { JumpLink } from '../today/shared';
import { AskAdmin } from '../AskAdmin';
import { BookingForm, ExtLink, FIND_3PAO_URL, Glyph, useToast } from './govramp-parts';
import '../../pages/govramp.css';

const CHIP: Record<RuleStatus, { text: string; tone: string } | null> = {
  done: null, not: { text: 'Not met', tone: 'red' }, noev: { text: 'No evidence', tone: 'outline' }, ns: { text: 'Not started', tone: 'neutral' }, act: { text: 'Needs action', tone: 'amber' },
};
const KIND_LABEL: Record<string, string> = {
  rar: 'Readiness Assessment Report', sap: 'Security Assessment Plan', sar: 'Security Assessment Report', soc2_report: 'SOC 2 Type II report',
  engagement_letter: 'Engagement letter', schedule: 'Schedule for the next report', bridge_letter: 'Bridge letter', supplemental: 'Supplemental evidence', other: 'Other',
};
const today = () => new Date().toISOString().slice(0, 10);

export function ClassAChecklist({ card, slug, prefix, canManage, tabHref }: {
  card: ProgramCard; slug: string; prefix: string; canManage: boolean; tabHref: (t: 'report') => string;
}) {
  const programs = usePrograms(slug);
  const govCard = programs.data?.programs.find((c) => c.program === 'govramp') ?? null;
  const st = useClassA(slug, card, govCard, { optional: true });
  const [toastNode, toast] = useToast();
  const [bookOpen, setBookOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState<null | 'govramp' | 'soc2_type2'>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [doneOpen, setDoneOpen] = useState(false);

  if (st.loading || programs.isLoading) return <Spinner label="Loading Class A" />;
  if (st.error || !st.world || !st.counts) {
    return <Alert variant="error"><p>Class A could not load.</p><Button onClick={() => st.refetch()}>Retry</Button></Alert>;
  }
  const w = st.world;
  const c = st.counts;
  const todayDay = (st.gov?.generated_at ?? new Date().toISOString()).slice(0, 10);
  const gs = st.gov ? govrampStatus(st.gov, todayDay) : null;
  const grBase = govCard ? `${prefix}/programs/${govCard.program}` : null;
  const gating = ['requires-ready', 'requires-ready-late', 'pursuing-ready', 'govramp-lapsed'].includes(w.kind);
  const tone = w.kind === 'materials-expired' || w.kind === 'govramp-lapsed' ? 'bad' : w.kind === 'materials-current' ? undefined : 'warn';
  const open = st.rules.filter((r) => r.status !== 'done');
  const done = st.rules.filter((r) => r.status === 'done');
  const famsDone = new Set(st.rules.map((r) => r.family ?? '')).size - new Set(open.map((r) => r.family ?? '')).size;

  // ONE primary per world.
  let primary: JSX.Element | null = null;
  if (w.kind === 'requires-ready' && grBase) primary = <Link className="gp-linkbtn btn-primary" to={`${grBase}?tier=ready`}>Plan Ready assessment</Link>;
  else if (w.kind === 'requires-ready-late' && grBase) primary = <Link className="gp-linkbtn btn-primary" to={`${grBase}#gr-conmon-h`}>{`Finish ${gs?.cycle?.label ?? 'ConMon'}`}</Link>;
  else if (w.kind === 'govramp-lapsed' && grBase) primary = <Link className="gp-linkbtn btn-primary" to={`${grBase}#gr-conmon-h`}>{gs?.cycle ? `Submit ${gs.cycle.label} · then contact GovRAMP` : 'Open GovRAMP'}</Link>;
  else if (w.kind === 'pursuing-ready' && !w.booking && govCard) {
    primary = canManage ? <Button variant="primary" aria-expanded={bookOpen} aria-controls="ca-book-form" onClick={() => setBookOpen(true)}>Record Ready booking</Button>
      : <AskAdmin slug={slug} compact what="runs the GovRAMP program" verb="record the Ready booking" subject="GAC Pro: record our GovRAMP Ready assessment booking" />;
  } else if (w.kind === 'materials-missing') primary = canManage ? <Button variant="primary" onClick={() => setUploadOpen('govramp')}>Upload RAR + SAP</Button> : null;
  else if (w.kind === 'materials-expired') primary = canManage ? <Button variant="primary" onClick={() => setUploadOpen('govramp')}>Upload newer RAR + SAP</Button> : null;
  else if (w.kind === 'materials-expiring' && grBase) primary = <Link className="gp-linkbtn btn-primary" to={`${grBase}/prepare?book=1#gr-rd-h`}>Book reassessment</Link>;
  const fillWork = !primary; // the work buttons carry the filled primary only when the world has none

  return (
    <div className="gr-page" data-part="class-a">
      {toastNode}
      <section className="gr-card" data-tone={tone} aria-labelledby="ca-h" data-part="class-a-hero">
        <h3 id="ca-h" className="gr-hero-label" tabIndex={-1}>Class A · required rules</h3>
        {w.alarm ? (
          <p className="gr-alarm" data-part="alarm" role={tone === 'bad' ? 'alert' : undefined}>
            <Glyph status={tone === 'bad' ? 'not' : w.kind === 'pursuing-ready' && w.booking ? 'act' : gating ? 'ns' : 'act'} />{w.alarm}
            {w.kind === 'pursuing-ready' && w.booking && canManage ? <button type="button" className="gr-mutedlink" onClick={() => setBookOpen(true)}>Change booking</button> : null}
          </p>
        ) : null}
        {w.consequence ? <p className="gr-consequence">{w.consequence}</p> : null}
        <p className="gr-hero-fig"><b className="gr-num" data-part="done-count">{c.done}</b><span className="gr-dim">{`of ${c.total} done`}</span></p>
        <div className="gr-meter" role="img" data-part="meter"
          aria-label={`${c.done} done, ${c.act ? `${c.act} needs action, ` : ''}${c.not} not met, ${c.noev} no evidence, ${c.ns} not started, of ${c.total}`}>
          {(['done', 'act', 'not', 'noev', 'ns'] as const).map((k) => (c[k] ? <span key={k} className={`gr-seg gr-seg--${k}`} style={{ flexGrow: c[k] }} /> : null))}
        </div>
        <p className="gr-legend" data-part="legend">
          {classALegend(c).map((l) => <span key={l.key}><i className={`gr-seg--${l.key}`} />{l.key === 'done' ? l.text : <JumpLink to="ca-list">{l.text}</JumpLink>}</span>)}
        </p>
        <div className="gr-actions" data-part="hero-actions">
          {primary}
          {c.fixLabel ? <JumpLink to="ca-list" className={`gp-linkbtn${fillWork ? ' btn-primary' : ''}`}>{c.fixLabel}</JumpLink> : null}
          {c.collectLabel ? <JumpLink to="ca-list" className="gp-linkbtn">{c.collectLabel}</JumpLink> : null}
          {c.startLabel ? <JumpLink to="ca-list" className="gp-linkbtn">{c.startLabel}</JumpLink> : null}
        </div>
        {bookOpen && govCard ? (
          <div id="ca-book-form">
            <BookingForm slug={slug} program={govCard.profile_id} kind="ready_booking" idPrefix="ca-book" initial={w.booking}
              onCancel={() => setBookOpen(false)} onDone={(m) => { setBookOpen(false); toast({ text: m, focusId: 'ca-h' }); }} />
          </div>
        ) : null}
        {gating ? (
          <div className="gr-actions">
            <JumpLink to="ca-mat-h" className="gr-textlink gr-small">Use a SOC 2 Type II report instead</JumpLink>
            {w.kind === 'pursuing-ready' ? <ExtLink href={FIND_3PAO_URL} label="Find a 3PAO on the GovRAMP site">Find a 3PAO</ExtLink> : null}
            {w.kind === 'requires-ready-late' ? <span className="gr-actions"><Button compact aria-disabled="true" aria-describedby="ca-pr-why">Plan Ready assessment</Button>
              <span id="ca-pr-why" className="gr-small gr-dim">{`After ${gs?.cycle?.label ?? 'ConMon'} is submitted`}</span></span> : null}
            {w.kind === 'govramp-lapsed' ? <span className="gr-small gr-dim">{`Plan Ready assessment · After ${tierLabel(gs?.held ?? st.gov?.tier)} is restored`}</span> : null}
          </div>
        ) : null}
      </section>

      <div className="gr-grid2">
        <Materials st={st} w={w} slug={slug} profileId={card.profile_id} canManage={canManage} uploadOpen={uploadOpen} setUploadOpen={setUploadOpen} onToast={toast} todayDay={todayDay} />
        <Clocks st={st} card={card} slug={slug} canManage={canManage} gating={gating} onToast={toast} todayDay={todayDay} />
      </div>

      <section className="gr-card" aria-labelledby="ca-list" data-part="class-a-list">
        <div className="gr-card-head">
          <h3 id="ca-list" className="gr-card-h" tabIndex={-1}>{`${c.total} required for Class A`}</h3>
          <span className="gr-seg-filter" role="group" aria-label="Show">
            <button type="button" aria-pressed={filter === 'open'} onClick={() => { setFilter('open'); setDoneOpen(false); }}>{`Needs action · ${open.length}`}</button>
            <button type="button" aria-pressed={filter === 'all'} onClick={() => { setFilter('all'); setDoneOpen(true); }}>{`All ${c.total}`}</button>
          </span>
        </div>
        <div>
          {open.map((r) => <RuleRow key={r.ref} r={r} prefix={prefix} />)}
          {done.length ? (
            <details className="gr-fold gr-divide-top" open={doneOpen} onToggle={(e) => setDoneOpen((e.currentTarget as HTMLDetailsElement).open)}>
              <summary>{`${done.length} done · ${famsDone} ${famsDone === 1 ? 'family' : 'families'} complete`}<span className="gr-small gr-dim">{doneOpen ? 'Hide' : 'Show'}</span></summary>
              {done.map((r) => <RuleRow key={r.ref} r={r} prefix={prefix} />)}
            </details>
          ) : null}
          <details className="gr-why"><summary>{`Rule ids for all ${c.total}`}</summary><div>
            {st.rules.map((r) => <span key={r.ref}><span className="gr-mono">{r.ref}</span>{` · ${r.title}`}</span>)}
          </div></details>
        </div>
        <div className="gr-actions gr-divide-top">
          {c.done === c.total && c.total > 0
            ? <Link className="gp-linkbtn btn-primary" to={tabHref('report')}>File Class A listing request</Link>
            : <><Button aria-disabled="true" aria-describedby="ca-lr-why">File Class A listing request</Button><span id="ca-lr-why" className="gr-small gr-dim">{`Opens at ${c.total} of ${c.total}`}</span></>}
        </div>
        {st.optional.length ? (
          <details className="gr-fold gr-divide-top">
            <summary>{`Optional · ${st.optional.length}`}<span className="gr-small gr-dim">{`${st.optional.filter((r) => r.status === 'done').length} done`}</span></summary>
            <p className="gr-small gr-dim">Not needed to file Class A.</p>
            {st.optional.map((r) => <RuleRow key={r.ref} r={r} prefix={prefix} />)}
          </details>
        ) : null}
      </section>
    </div>
  );
}

function RuleRow({ r, prefix }: { r: ClassARule; prefix: string }) {
  const chip = CHIP[r.status];
  const act = r.status === 'done' ? null
    : r.waits
      ? <JumpLink to={r.waits === 'materials' ? 'ca-mat-h' : 'ca-clk-h'} className="gr-textlink gr-small">{r.waits === 'class_b' ? 'Record Class B booking' : r.waits === 'listing' ? 'Set listing date' : 'Go to prerequisite'}</JumpLink>
      : r.status === 'not' || r.status === 'act'
        ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/findings?requirement=${encodeURIComponent(r.ref)}`} aria-label={`Fix ${r.isKsi ? r.ref : r.title}`}>Fix</Link>
        : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/evidence`} aria-label={`${r.status === 'noev' ? 'Upload evidence for' : 'Start'} ${r.isKsi ? r.ref : r.title}`}>{r.status === 'noev' ? 'Upload' : 'Start'}</Link>;
  return (
    <div className="gr-ck" data-done={r.status === 'done'} data-part="class-a-rule">
      <Glyph status={r.status} />
      <span className="gr-overline">{r.family ?? ''}</span>
      <span className="gr-ck-t">{r.isKsi ? <span className="gr-ctl">{r.ref}</span> : null}{r.title}</span>
      <span className="gr-ck-m">{chip ? <span className={`gp-chip gp-chip--${chip.tone}`}>{chip.text}</span> : <span className="gp-sr-only">Done · </span>}{chip && chip.text === r.meta ? null : r.meta}</span>
      <span className="gr-ck-a">{act}</span>
    </div>
  );
}

function Materials({ st, w, slug, profileId, canManage, uploadOpen, setUploadOpen, onToast, todayDay }: {
  st: ReturnType<typeof useClassA>; w: NonNullable<ReturnType<typeof useClassA>['world']>; slug: string; profileId: string; canManage: boolean;
  uploadOpen: null | 'govramp' | 'soc2_type2'; setUploadOpen: (v: null | 'govramp' | 'soc2_type2') => void; onToast: ReturnType<typeof useToast>[1]; todayDay: string;
}) {
  const list = useQuery({ queryKey: queryKeys.alternativeMaterials(slug, profileId), queryFn: () => listAlternativeMaterials(slug, profileId), retry: false, staleTime: 30_000 });
  const m = st.materials;
  const gr = m?.frameworks.find((f) => f.framework === (m.basis_framework ?? 'govramp')) ?? m?.frameworks.find((f) => f.framework === 'govramp') ?? null;
  const rows = (list.data ?? []).filter((x) => x.framework === (gr?.framework ?? 'govramp'));
  const assessed = rows.map((x) => x.assessed_at).sort().pop() ?? null;
  const gating = ['requires-ready', 'requires-ready-late', 'pursuing-ready', 'govramp-lapsed'].includes(w.kind);
  const chip = w.kind === 'materials-current' ? { t: '✓ Current', tone: 'ok' } : w.kind === 'materials-expiring' && m?.expires_at ? { t: `! Expires ${fmtShort(m.expires_at, todayDay)}`, tone: 'amber' }
    : w.kind === 'materials-expired' ? { t: '✕ Expired', tone: 'red' } : w.kind === 'govramp-lapsed' ? { t: '✕ GovRAMP lapsed', tone: 'red' }
      : gating ? { t: 'Requires GovRAMP Ready', tone: 'outline' } : { t: 'Not uploaded', tone: 'outline' };
  return (
    <section className="gr-card" aria-labelledby="ca-mat-h" data-part="class-a-materials">
      <div className="gr-card-head">
        <h3 id="ca-mat-h" className="gr-card-h" tabIndex={-1}>{`Prerequisite · ${gr?.framework === 'soc2_type2' ? 'SOC 2 Type II report' : 'GovRAMP Ready materials'}`}</h3>
        <span className={`gp-chip gp-chip--${chip.tone}`}>{chip.t}</span>
      </div>
      {m && (m.state === 'met' || m.state === 'expired') && m.expires_at ? (
        <p className="gr-small">{`${gr?.label ?? 'Assessment'} materials${assessed ? ` · assessed ${fmtLong(assessed)}` : ''} · usable until `}
          {m.state === 'expired' ? <><span className="gr-struck">{fmtLong(m.expires_at)}</span>{' '}<span className="gr-bad">expired</span></>
            : <b className={w.kind === 'materials-expiring' ? 'gr-warn' : undefined}>{fmtLong(m.expires_at)}</b>}</p>
      ) : (
        <p className="gr-small">{w.kind === 'requires-ready' ? `${tierLabel(st.gov?.held?.tier ?? st.gov?.tier)} held · Class A needs the RAR + SAP from a GovRAMP Ready assessment`
          : w.kind === 'pursuing-ready' ? `Pursuing Ready · ${w.booking ? `Ready assessment booked ${fmtShort(w.booking.date, todayDay)}` : 'Ready assessment not booked'}`
            : w.kind === 'requires-ready-late' ? 'Ready planning opens once the late ConMon is submitted'
              : w.kind === 'govramp-lapsed' ? 'Ready planning opens after GovRAMP is restored'
                : 'RAR + SAP not uploaded'}</p>
      )}
      {rows.length ? rows.map((x) => (
        <div key={x.material_id} className="gr-doc">
          <span><b>{x.title ?? KIND_LABEL[x.kind] ?? x.kind}</b><br /><span className="gr-small gr-dim">{`${KIND_LABEL[x.kind] ?? x.kind} · uploaded ${fmtShort(x.created_at, todayDay)}`}</span></span>
          <Link className="gr-mutedlink" to={`/w/${slug}/app/evidence`} aria-label={`Open ${x.title ?? KIND_LABEL[x.kind] ?? x.kind}`}>Open</Link>
        </div>
      )) : null}
      {uploadOpen ? (
        <MaterialUpload slug={slug} profileId={profileId} framework={uploadOpen} supersedes={rows} onCancel={() => setUploadOpen(null)}
          onDone={(msg) => { setUploadOpen(null); onToast({ text: msg, focusId: 'ca-mat-h' }); }} />
      ) : canManage ? (
        <div className="gr-actions">
          {gating ? <div className="gr-doc"><span><b>Or · SOC 2 Type II report</b><br /><span className="gr-small gr-dim">Accepted instead of Ready materials · not uploaded</span></span>
            <Button compact onClick={() => setUploadOpen('soc2_type2')}>Upload SOC 2 Type II report</Button></div>
            : <>
              <Button compact onClick={() => setUploadOpen('govramp')}>{rows.length ? 'Replace a material' : 'Upload RAR + SAP'}</Button>
              <button type="button" className="gr-mutedlink" onClick={() => setUploadOpen('soc2_type2')}>Use a SOC 2 Type II report instead</button>
            </>}
        </div>
      ) : <AskAdmin slug={slug} compact what="runs the FedRAMP program" verb="upload the Class A materials" subject="GAC Pro: upload our FedRAMP Class A materials" />}
      <details className="gr-why"><summary>Why these materials</summary><div>
        <span>Class A accepts a 3PAO Readiness Assessment Report + Security Assessment Plan from GovRAMP Ready, usable for 12 months after the assessment, or a SOC 2 Type II report.</span>
        {m ? <span className="gr-mono">{m.rule_ids.join(' · ')}</span> : null}
      </div></details>
    </section>
  );
}

function MaterialUpload({ slug, profileId, framework, supersedes, onCancel, onDone }: {
  slug: string; profileId: string; framework: 'govramp' | 'soc2_type2';
  supersedes: ReadonlyArray<{ material_id: string; kind: string; framework: string }>; onCancel: () => void; onDone: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const kinds = framework === 'govramp' ? ['rar', 'sap', 'sar', 'supplemental'] : ['soc2_report', 'engagement_letter', 'schedule', 'bridge_letter', 'supplemental'];
  const [kind, setKind] = useState(kinds[0]!);
  const [file, setFile] = useState<File | null>(null);
  const [assessed, setAssessed] = useState('');
  const [errs, setErrs] = useState<{ file?: string; date?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: typeof errs = {};
    if (!file) next.file = 'Choose a file.';
    if (!assessed) next.date = 'Choose the assessment date.';
    else if (assessed > today()) next.date = 'The assessment date can’t be in the future.';
    setErrs(next);
    if (Object.keys(next).length) return;
    setBusy(true); setError(null);
    try {
      const old = supersedes.find((x) => x.framework === framework && x.kind === kind);
      await uploadAlternativeMaterial(slug, profileId, { file: file!, framework, kind, assessedAt: assessed, supersedes: old?.material_id });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      onDone(`${KIND_LABEL[kind] ?? kind} uploaded`);
    } catch (ex) { setError(ex instanceof Error ? ex.message : 'The upload failed.'); }
    finally { setBusy(false); }
  };
  return (
    <form className="gr-recform" onSubmit={submit} noValidate aria-label="Upload a Class A material" data-part="material-upload"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } }}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Field id="ca-up-kind" label="Document">
        <Select value={kind} onChange={(e) => setKind(e.currentTarget.value)}>{kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] ?? k}</option>)}</Select>
      </Field>
      <Field id="ca-up-file" label="File" required error={errs.file} hint="PDF, Word, Markdown or text · up to 25 MB">
        <Input type="file" accept=".pdf,.docx,.doc,.md,.txt" onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)} />
      </Field>
      <Field id="ca-up-date" label="Assessment date" required error={errs.date}>
        <Input type="date" value={assessed} max={today()} onChange={(e) => setAssessed(e.currentTarget.value)} />
      </Field>
      <span className="gr-actions">
        <Button type="submit" variant="primary" compact aria-disabled={busy || undefined}>{busy ? 'Uploading…' : 'Upload'}</Button>
        <Button variant="ghost" compact onClick={onCancel}>Cancel</Button>
      </span>
    </form>
  );
}

function Clocks({ st, card, slug, canManage, gating, onToast, todayDay }: {
  st: ReturnType<typeof useClassA>; card: ProgramCard; slug: string; canManage: boolean; gating: boolean; onToast: ReturnType<typeof useToast>[1]; todayDay: string;
}) {
  const qc = useQueryClient();
  const events = useQuery({ queryKey: ['class-a', slug, 'events', card.profile_id], queryFn: () => listProgramEvents(slug, card.profile_id, ['higher_class_booking']), retry: false, staleTime: 30_000 });
  const booking = events.data?.[0] ?? null;
  // V103-6-3: the inline form edits the TARGET listing date (a plan; future allowed). The ACTUAL listing
  // date is an event (never in the future), recorded once FedRAMP lists the offering.
  const [form, setForm] = useState<null | 'target' | 'actual'>(null);
  const [date, setDate] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cbOpen, setCbOpen] = useState(false);
  const use = st.clocks.find((c) => c.key === 'class_a_agency_use') ?? null;
  const bcd = st.clocks.find((c) => c.key === 'class_a_bcd_assessment') ?? null;
  const projected = !st.listingDate && !!st.targetDate;
  const open = (which: 'target' | 'actual') => { setForm(which); setErr(null); setDate(which === 'target' ? st.targetDate ?? '' : ''); };
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !form) return;
    if (!date) { setErr(form === 'target' ? 'Choose the target listing date.' : 'Choose the listing date.'); document.getElementById('ca-ld')?.focus(); return; }
    if (form === 'actual' && date > today()) { setErr('The listing date can’t be in the future; set a target instead.'); document.getElementById('ca-ld')?.focus(); return; }
    setErr(null); setBusy(true);
    try {
      await setClassAListing(slug, card.profile_id, form === 'target' ? { class_a_target_listing_date: date } : { class_a_listing_date: date });
      await Promise.all(invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })));
      onToast({ text: form === 'target' ? 'Target listing date saved' : 'Listing date recorded', focusId: 'ca-clk-h' });
      setForm(null);
    } catch (ex) { setErr(ex instanceof Error ? ex.message : 'The date did not save.'); }
    finally { setBusy(false); }
  };
  const dateForm = form ? (
    <form className="gr-recform" onSubmit={save} noValidate aria-label={form === 'target' ? 'Set the target listing date' : 'Record the listing date'} data-part={form === 'target' ? 'target-form' : 'listing-form'}>
      <Field id="ca-ld" label={form === 'target' ? 'Target listing date' : 'Listing date'} required error={err ?? undefined} hint="Owner, admin or program lead">
        <Input type="date" value={date} max={form === 'actual' ? today() : undefined} onChange={(e) => setDate(e.currentTarget.value)} />
      </Field>
      <span className="gr-actions"><Button type="submit" compact aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Save'}</Button>
        <Button variant="ghost" compact onClick={() => setForm(null)}>Cancel</Button></span>
    </form>
  ) : null;
  return (
    <section className="gr-card" aria-labelledby="ca-clk-h" data-part="class-a-clocks">
      <h3 id="ca-clk-h" className="gr-card-h" tabIndex={-1}>Class A clocks</h3>
      <div className="gr-clock" data-part="clock-listing">
        <span className="gr-small gr-dim">Listing date</span>
        {st.listingDate ? <b>{fmtLong(st.listingDate)}</b> : st.targetDate ? <><b>{fmtLong(st.targetDate)}</b><span className="gr-small gr-dim">{`target${gating ? ' · depends on Ready assessment' : ''}`}</span></> : <b>Not set</b>}
        {canManage ? (dateForm ?? (
          <span className="gr-actions">
            {st.listingDate ? null : <button type="button" className="gr-mutedlink" onClick={() => open('target')}>{st.targetDate ? 'Change listing date' : 'Set a target listing date'}</button>}
            <button type="button" className="gr-mutedlink" onClick={() => open('actual')}>{st.listingDate ? 'Change the recorded listing date' : 'Record the listing date'}</button>
          </span>
        )) : <AskAdmin slug={slug} compact what="runs the FedRAMP program" verb="set the listing date" subject="GAC Pro: set our FedRAMP Class A listing date" />}
      </div>
      <div className="gr-clock" data-part="clock-use">
        <span className="gr-small gr-dim">Agencies can use Class A until</span>
        <b>{use?.due_at ? fmtLong(use.due_at) : '—'}</b>
        {use?.due_at ? <span className="gr-small gr-dim">{use.state === 'overdue' ? 'Past' : `${projected ? 'If listed on the target date · ' : ''}${daysText(Math.round((Date.parse(use.due_at) - Date.parse(todayDay)) / 86_400_000))} · extends while a Class B assessment is scheduled`}</span>
          : <span className="gr-small gr-dim">{use?.state === 'rule_pending' ? 'Waiting on the reviewed rule' : 'From the listing date'}</span>}
      </div>
      <div className="gr-clock" data-part="clock-class-b">
        <span className="gr-small gr-dim">Class B assessment</span>
        <b>{booking ? `Booked · ${fmtLong((booking.data as { assessment_date: string }).assessment_date)}` : 'Not yet booked'}</b>
        {booking ? <span className="gr-small gr-dim">{`${(booking.data as { firm: string }).firm}${bcd?.due_at ? ` · due by ${fmtLong(bcd.due_at)}` : ''}`}</span> : bcd?.due_at ? <span className="gr-small gr-dim">{`Due by ${fmtLong(bcd.due_at)}`}</span> : null}
        {canManage ? (cbOpen
          ? <BookingForm slug={slug} program={card.profile_id} kind="higher_class_booking" idPrefix="ca-cb" initial={booking ? { firm: (booking.data as { firm: string }).firm, date: (booking.data as { assessment_date: string }).assessment_date } : null}
              onCancel={() => setCbOpen(false)} onDone={(m) => { setCbOpen(false); onToast({ text: m, focusId: 'ca-clk-h' }); }} />
          : <button type="button" className="gr-mutedlink" onClick={() => setCbOpen(true)}>{booking ? 'Change' : 'Record Class B booking'}</button>) : null}
      </div>
      <details className="gr-why"><summary>Why these dates</summary><div>
        <span>Class A is usable for 12 months from listing; it extends while a Class B or C assessment is scheduled. A Class B, C or D assessment is scheduled within 2 years of listing.</span>
        {st.clocks.length ? <span className="gr-mono">{st.clocks.map((c) => c.rule_id).join(' · ')}</span> : null}
      </div></details>
    </section>
  );
}
